import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import api from '../../services/api';
import RichQuestionEditor from './RichQuestionEditor';

// Shared react-select styling — mirrors other modals in the app (see
// AllocationModal) so the Question Bank dropdowns match the rest of the UI.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#3949ab' : '#e0e0e0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26, 35, 126, 0.1)' : 'none',
    '&:hover': { borderColor: '#3949ab' },
    fontSize: 14,
    minHeight: 38,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? '#1a237e' : state.isFocused ? '#e8eaf6' : 'white',
  }),
  placeholder: (base) => ({ ...base, color: '#9e9e9e' }),
  // Raise above the modal body's local stacking context.
  menu: (base) => ({ ...base, zIndex: 60 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// Map attribute code -> display label + server-side field name on the question.
// Matches COLUMN_TO_SEGMENT in templateSchema so master values flow through.
const ATTRIBUTES = [
  { code: 'co',         label: 'CO',                         segment: 'course_outcomes',     field: 'co' },
  { code: 'unit',       label: 'Unit',                       segment: 'unit',                 field: 'unit' },
  { code: 'bl',         label: "Bloom's Level (BL)",         segment: 'learning_taxonomy',    field: 'bl' },
  { code: 'type',       label: 'Question Type',              segment: 'question_type',        field: 'type' },
  { code: 'difficulty', label: 'Difficulty Level',           segment: 'difficulty_level',     field: 'difficulty' },
  { code: 'kp',         label: 'Knowledge Priority (KP)',    segment: 'knowledge_priority',   field: 'kp' },
  { code: 'po',         label: 'PO',                         segment: 'program_outcomes',     field: 'po' },
];

/**
 * Create / edit a single question.
 *
 * Attribute dropdowns are rendered only for attributes enabled on Question
 * Bank Settings (from QBAttributeSettings). Each dropdown is populated from
 * the QP Master Values segment that owns the attribute — same vocabulary
 * that drives QP Template column values.
 */
function QuestionEditorModal({
  tenantId,
  questionId,
  defaultCourseId,
  courses,
  segments,
  enabledAttributes,
  onClose,
  onSaved,
}) {
  const isEdit = !!questionId;

  const [form, setForm] = useState({
    courseId: defaultCourseId || '',
    segmentId: segments?.[0]?.SegmentID || '',
    questionHtml: '',
    marks: 1,
    co: '',
    unit: '',
    bl: '',
    type: '',
    difficulty: '',
    kp: '',
    po: '',
    isActive: true,
  });

  const [masterOptions, setMasterOptions] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  // Only fetch master values for attributes actually shown in this editor.
  const visibleAttributes = useMemo(
    () => ATTRIBUTES.filter((a) => enabledAttributes.includes(a.code)),
    [enabledAttributes]
  );

  // react-select option lists + currently-selected option (memoised so
  // identity stays stable between renders — avoids re-mounting the menu).
  const courseOptions = useMemo(
    () =>
      (courses || []).map((c) => ({
        value: c.CourseID,
        label: `${c.CourseCode} — ${c.CourseName}`,
      })),
    [courses]
  );
  const segmentOptions = useMemo(
    () =>
      (segments || []).map((s) => ({
        value: s.SegmentID,
        label: s.SegmentName,
      })),
    [segments]
  );
  const selectedCourse = useMemo(
    () => courseOptions.find((o) => String(o.value) === String(form.courseId)) || null,
    [courseOptions, form.courseId]
  );
  const selectedSegment = useMemo(
    () => segmentOptions.find((o) => String(o.value) === String(form.segmentId)) || null,
    [segmentOptions, form.segmentId]
  );

  // -------------------- Load master values --------------------
  useEffect(() => {
    if (!tenantId || visibleAttributes.length === 0) return;
    (async () => {
      try {
        const results = await Promise.all(
          visibleAttributes.map((a) =>
            api
              .get(`/qp-settings/master-values/${tenantId}`, {
                params: { segmentCode: a.segment },
              })
              .then((r) => (r.data?.success ? r.data.data || [] : []))
              .catch(() => [])
          )
        );
        const opts = {};
        visibleAttributes.forEach((a, i) => {
          opts[a.code] = (results[i] || [])
            .filter((v) => v.IsActive)
            .map((v) => v.ValueName);
        });
        setMasterOptions(opts);
      } catch {
        // Non-fatal — dropdowns fall back to free text.
      }
    })();
  }, [tenantId, visibleAttributes]);

  // -------------------- Load existing question --------------------
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/question-bank/questions/detail/${questionId}`);
        if (res.data.success) {
          const q = res.data.data;
          setForm({
            courseId: q.CourseID,
            segmentId: q.SegmentID,
            questionHtml: q.QuestionHtml || '',
            marks: Number(q.Marks) || 0,
            co: q.CO || '',
            unit: q.Unit || '',
            bl: q.BL || '',
            type: q.QType || '',
            difficulty: q.Difficulty || '',
            kp: q.KP || '',
            po: q.PO || '',
            isActive: !!q.IsActive,
          });
        }
      } catch {
        toast.error('Failed to load question');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, questionId]);

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.courseId) return toast.error('Course is required');
    if (!form.segmentId) return toast.error('Segment is required');
    const plain = (form.questionHtml || '').replace(/<[^>]+>/g, '').trim();
    if (!plain) return toast.error('Question text is required');
    if (Number.isNaN(Number(form.marks))) return toast.error('Marks must be a valid number');

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/question-bank/questions/${questionId}`, { ...form, tenantId });
        toast.success('Question updated');
      } else {
        await api.post('/question-bank/questions', { ...form, tenantId });
        toast.success('Question saved');
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const renderAttributeSelect = (attr) => {
    const opts = masterOptions[attr.code] || [];
    return (
      <div className="form-group" key={attr.code}>
        <label>{attr.label}</label>
        {opts.length > 0 ? (
          <select
            value={form[attr.field]}
            onChange={(e) => setField(attr.field, e.target.value)}
          >
            <option value="">— Select —</option>
            {opts.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={form[attr.field]}
            placeholder="Free text (no master values configured)"
            onChange={(e) => setField(attr.field, e.target.value)}
          />
        )}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content qb-editor-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Question' : 'Add Question'}</h2>
          <button className="btn btn-close" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading…</div>
        ) : (
          <div className="modal-body">
            <div className="qb-editor-row">
              <div className="form-group" style={{ flex: 2, minWidth: 260 }}>
                <label className="required-label">Course</label>
                <Select
                  classNamePrefix="rs"
                  options={courseOptions}
                  value={selectedCourse}
                  onChange={(opt) => setField('courseId', opt ? opt.value : '')}
                  isDisabled={isEdit}
                  isClearable={!isEdit}
                  isSearchable
                  placeholder="Search / select course..."
                  noOptionsMessage={() => 'No courses match your search'}
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  menuPosition="fixed"
                />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: 200 }}>
                <label className="required-label">Segment</label>
                <Select
                  classNamePrefix="rs"
                  options={segmentOptions}
                  value={selectedSegment}
                  onChange={(opt) => setField('segmentId', opt ? opt.value : '')}
                  isSearchable={segmentOptions.length > 5}
                  placeholder="Select segment..."
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  menuPosition="fixed"
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="required-label">Marks</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.marks}
                  onChange={(e) => setField('marks', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="required-label">Question</label>
              <RichQuestionEditor
                value={form.questionHtml}
                onChange={(v) => setField('questionHtml', v)}
                placeholder="Type the question here. Use ƒ for math / chemistry formulas and 🖼 to add diagrams."
              />
            </div>

            {visibleAttributes.length > 0 && (
              <>
                <div className="qb-editor-subhead">Attributes</div>
                <div className="qb-editor-attr-grid">
                  {visibleAttributes.map(renderAttributeSelect)}
                </div>
              </>
            )}

            <div className="qb-editor-row" style={{ alignItems: 'center', marginTop: 4 }}>
              <label
                className="toggle-switch"
                style={{ marginRight: 8 }}
                title={form.isActive ? 'Active' : 'Inactive'}
              >
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setField('isActive', e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
              <span style={{ fontSize: 13, color: '#424242' }}>
                {form.isActive
                  ? 'Active — eligible for inclusion in question papers.'
                  : 'Inactive — retained in the bank but not offered during paper generation.'}
              </span>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn btn-save" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving…' : isEdit ? 'Update Question' : 'Save Question'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuestionEditorModal;
